// Import necessary modules from d3 library
import * as d3 from "d3";
import * as d3Sankey from "d3-sankey";

// Define the SankeyFun function as the default export
export default function SankeyFun(
  { links, svgRef },
  {
    // Destructure input parameters with default values
    nodes,
    format = ",",
    align = "left",
    nodeId = (d) => d.id,
    nodeGroup,
    nodeGroups,
    nodeLabel,
    nodeTitle = (d) => `${d.id}\n${(d.value * 100)?.toFixed(2)}%`,
    nodeAlign = align,
    nodeWidth = 15,
    nodePadding = 30,
    nodeLabelPadding = 6,
    nodeStroke = "currentColor",
    nodeStrokeWidth,
    nodeStrokeOpacity,
    nodeStrokeLinejoin,
    linkSource = ({ source }) => source,
    linkTarget = ({ target }) => target,
    linkValue = ({ value }) => value,
    linkPath = d3Sankey.sankeyLinkHorizontal(),
    linkTitle = (d) =>
      `${d.source.id} → ${d.target.id}\n${format((d.value * 100).toFixed(2))}%`,
    linkColor = "source-target",
    linkStrokeOpacity = 0.5,
    linkMixBlendMode = "multiply",
    colors = d3.schemeTableau10,
    width = 640,
    height = 400,
    marginTop = 5,
    marginRight = 1,
    marginBottom = 5,
    marginLeft = 1,
  } = {},
  colorMapData
) {
  // Function to determine node alignment based on input
  if (typeof nodeAlign !== "function")
    nodeAlign =
      {
        left: d3Sankey.sankeyLeft,
        right: d3Sankey.sankeyRight,
        center: d3Sankey.sankeyCenter,
      }[nodeAlign] ?? d3Sankey.sankeyLeft;

  // Create arrays for link source, target, and value
  const LS = d3.map(links, linkSource).map(intern);
  const LT = d3.map(links, linkTarget).map(intern);
  const LV = d3.map(links, linkValue);

  // If nodes are not provided, generate nodes from links
  if (nodes === undefined)
    nodes = Array.from(d3.union(LS, LT), (id) => ({ id }));

  // Create arrays for node IDs, nodes, and node groups
  const N = d3.map(nodes, nodeId).map(intern);
  const G = nodeGroup == null ? null : d3.map(nodes, nodeGroup).map(intern);

  // Convert nodes and links to the required format
  nodes = d3.map(nodes, (_, i) => ({ id: N[i] }));
  links = d3.map(links, (_, i) => ({
    source: LS[i],
    target: LT[i],
    value: LV[i],
  }));

  // If nodeGroup is not provided, use "currentColor" for link colors
  if (!G && ["source", "target", "source-target"].includes(linkColor))
    linkColor = "currentColor";

  // If nodeGroups are not provided, use the generated node groups
  if (G && nodeGroups === undefined) nodeGroups = G;

  // Create a color scale based on the provided colorMap
  const colorMap = colorMapData;
  const color = d3.scaleOrdinal(Object.keys(colorMap), Object.values(colorMap));


  
  // Create a Sankey layout
  const sankeyLayout = d3Sankey
    .sankey()
    .nodeId(({ index: i }) => N[i])
    .nodeAlign(nodeAlign)
    .nodeWidth(nodeWidth)
    .nodePadding(nodePadding)
    .extent([
      [marginLeft, marginTop],
      [width - marginRight, height - marginBottom],
    ]);

  // Apply the Sankey layout to nodes and links
  sankeyLayout({ nodes, links });

  // If format is not a function, convert it to a d3 format
  if (typeof format !== "function") format = d3.format(format);

  // Group nodes by layer (x-position)
  const nodesByLayer = d3.group(nodes, (d) => d.x0);

  // Calculate percentages for each layer
  nodesByLayer.forEach((layerNodes) => {
    const layerTotal = d3.sum(layerNodes, (d) => d.value);
    layerNodes.forEach((node) => {
      node.percentage = ((node.value / layerTotal) * 100).toFixed(2);
    });
  });

  // Create arrays for node labels, node titles, and link titles
  const Tl =
    nodeLabel === undefined
      ? N
      : nodeLabel == null
      ? null
      : d3.map(nodes, nodeLabel);
  const Tt = nodeTitle == null ? null : d3.map(nodes, nodeTitle);
  const Lt = linkTitle == null ? null : d3.map(links, linkTitle);

  // Generate a unique ID for the SVG element
  const uid = `O-${Math.random().toString(16).slice(2)}`;

  // Select the SVG element using the provided ref
  const svg = d3.select(svgRef.current);

  // Remove any existing SVG elements
  svg.selectAll("*").remove();

  // Set SVG attributes
  svg
    .attr("width", width)
    .attr("height", height)
    .attr("viewBox", [0, 0, width, height])
    .attr("style", "max-width: 100%; height: auto; height: intrinsic;");

  // Create rectangles for nodes
  const node = svg
    .append("g")
    .attr("stroke-width", nodeStrokeWidth)
    .attr("stroke-opacity", nodeStrokeOpacity)
    // .attr("strokeLinejoin", nodeStrokeLinejoin)
    .selectAll("rect")
    .data(nodes)
    .join("rect")
    .attr("x", (d) => d.x0)
    .attr("y", (d) => d.y0)
    .attr("height", (d) => d.y1 - d.y0)
    .attr("width", (d) => d.x1 - d.x0)
    .attr("cursor", "pointer")
    

  // Identify and color the rightmost nodes
  node.attr("fill", (d) => {
    // Check if this is a rightmost node
    const isRightmostNode = d.x1 >= width - marginRight;
    return isRightmostNode ? color(G[d.index]) : color(G[d.index]);
  });

  

  // Add titles to nodes if provided
 if (Tt) node.append("title").text(({ index: i }) => Tt[i])
  

  // Create groups for links
  const link = svg
    .append("g")
    .attr("fill", "none")
    .attr("stroke-opacity", linkStrokeOpacity)
    .selectAll("g")
    .data(links)
    .join("g")
    .style("mix-blend-mode", linkMixBlendMode);

  // Define link colors and widths
  if (linkColor === "source-target")
    link
      .append("linearGradient")
      .attr("id", (d) => `${uid}-link-${d.index}`)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", (d) => d.source.x1)
      .attr("x2", (d) => d.target.x0)
      .call((gradient) =>
        gradient
          .append("stop")
          .attr("offset", "0%")
          .attr("stop-color", ({ source: { index: i } }) => color(G[i]))
      )
      .call((gradient) =>
        gradient
          .append("stop")
          .attr("offset", "100%")
          .attr("stop-color", ({ target: { index: i } }) => color(G[i]))
      );

  // Create paths for links
  link
    .append("path")
    .attr("d", linkPath)
    .attr(
      "stroke",
      linkColor === "source-target"
        ? ({ index: i }) => `url(#${uid}-link-${i})`
        : linkColor === "source"
        ? ({ source: { index: i } }) => color(G[i])
        : linkColor === "target"
        ? ({ target: { index: i } }) => color(G[i])
        : linkColor
    )
    .attr("stroke-width", ({ width }) => Math.max(1, width))
    // .call(
    //   Lt
    //     ? (path) => path.append("title").text(({ index: i }) => Lt[i])
    //     : () => {}
.on("mouseover", function(event, d) { // 1. FIX: Use 'd' here, do not destructure yet
  

  svg.selectAll("text")
    .filter((textData) => 
      // Check if the text belongs to the source OR target of this link
      textData.index === d.source.index || textData.index === d.target.index
    )
    .transition()
    .duration(200)
    .attr("font-size", "20px")
    .attr("font-weight", "bold");

  // 3. Tooltip Logic
  if (!Lt) return;

  let tooltip = d3.select("#sankey-custom-tooltip");
  if (tooltip.empty()) {
    tooltip = d3.select("body").append("div")
      .attr("id", "sankey-custom-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "rgba(0, 0, 0, 0.8)")
      .style("color", "#fff")
      .style("padding", "8px 12px")
      .style("border-radius", "4px")
      .style("font-family", "sans-serif")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("z-index", "999")
      .style("box-shadow", "0px 4px 8px rgba(0,0,0,0.3)");
  }


  tooltip.html(Lt[d.index].replace(/\n/g, "<br>"))
    .style("visibility", "visible")
    .style("top", (event.pageY - 10) + "px")
    .style("left", (event.pageX + 10) + "px");
})
.on("mousemove", function(event) {
  d3.select("#sankey-custom-tooltip")
    .style("top", (event.pageY - 10) + "px")
    .style("left", (event.pageX + 10) + "px");
})
.on("mouseout", function(event, d) { 
  
  // Action 1: Hide Tooltip
  d3.select("#sankey-custom-tooltip").style("visibility", "hidden");

  // Action 2: Reset Text Size
  svg.selectAll("text")
    .filter((textData) => 
      textData.index === d.source.index || textData.index === d.target.index
    )
    .transition()
    .duration(200)
    .attr("font-size", "16px")
    .attr("font-weight", "normal");
});


  // Add labels to nodes if provided
  if (Tl)
    svg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 16)
      .attr("font-weight", "normal")
      .selectAll("text")
      .data(nodes)
      .join("text")
      .attr("id", (d) => "node-text-" + d.index)
      .attr("x", (d) =>
        d.x0 < width / 2 ? d.x1 + nodeLabelPadding : d.x0 - nodeLabelPadding
      )
      .attr("y", (d) => (d.y1 + d.y0) / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", (d) => (d.x0 < width / 2 ? "start" : "end"))
      .text((d, i) => Tl[i] + ", " + (d.value * 100).toFixed(2) + "%")

  // .text((d, i ) => {

  //   return `${Tl[i]}, ${d.percentage}%`});



  // Helper function to intern values
  function intern(value) {
    return value !== null && typeof value === "object"
      ? value.valueOf()
      : value;
  }

  
  // Attach the color scale to the SVG node
  Object.assign(svg.node(), { scales: { color } });
}
